import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fileUrl } from "@/lib/api";
import { Crown, ShieldCheck, HandPointing, PencilSimple, Check } from "@phosphor-icons/react";

const PLACEHOLDER_ROLE_OPTIONS = ["Mother", "Father", "Son", "Daughter", "Spouse", "Sibling"];

/**
 * Shared detail dialog used by Members tab + Connection Map nodes.
 * `member` shape merges fields from the members list and the graph nodes,
 * so we read defensively.
 *
 * When the node is an unclaimed placeholder, we show a confirmation prompt
 * ("Is this you — [Name]?") that calls `onClaim` so the parent can wire it
 * to POST /placeholders/{id}/claim.
 *
 * Fix 17 — when the viewer is the creator of the placeholder (or the circle
 * owner / admin), an Edit button toggles an inline edit form that PATCHes
 * /placeholders/{pid}.
 */
export default function MemberDetailDialog({ member, open, onOpenChange, onClaim, claiming = false, onConfirm, onApprove, onReject, onEdit, savingEdit = false, currentUserId, isOwner = false, isAdmin = false, confirming = false, approving = false }) {
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", role_or_relationship: "", death_year: "" });

  // Reset edit form whenever a new member is opened.
  useEffect(() => {
    if (member) {
      setEditing(false);
      setEditForm({
        name: member.name || "",
        role_or_relationship: member.role_or_relationship || "",
        death_year: member.death_year || "",
      });
    }
  }, [member?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!member) return null;
  const initial = (member.name || "?").charAt(0).toUpperCase();
  const photo = member.profile_photo_path || member.photo_path;
  const role = member.role;
  const branch = member.branch;
  const relationship = member.relationship; // present on graph nodes & connections
  const subline = relationship && relationship !== "You" ? relationship : member.email;
  const isMemorial = member.is_memorial;
  const isPlaceholder = member.is_placeholder || member.is_living;
  const isClaimed = !!member.is_claimed;
  const isOpenSeat = isPlaceholder && !isClaimed;
  const memberType = member.member_type;
  // Fix 6 — community verification state (only meaningful for family placeholders).
  const confirmations = member.confirmations || [];
  const confirmationsCount = member.confirmations_count ?? confirmations.length;
  const confirmationsRequired = member.confirmations_required || 5;
  const isCommunityVerified = !!member.is_community_verified;
  const alreadyConfirmed = !!(currentUserId && confirmations.includes(currentUserId));
  const isCreator = !!(currentUserId && member.created_by === currentUserId);
  const showConfirmBlock = isOpenSeat && memberType === "family" && !!onConfirm;
  // Fix 5 — owner approval block for extended-family placeholders.
  const approvalStatus = member.approval_status || "approved";
  const isPendingApproval = isOpenSeat && memberType === "family" && approvalStatus === "pending_owner_approval";
  const showApprovalBlock = isPendingApproval && isOwner && (!!onApprove || !!onReject);
  // Fix 17 — inline edit only applies to family placeholders, and only when
  // the viewer created the placeholder OR is the circle owner / admin.
  const canEdit = isPlaceholder && memberType === "family" && !!onEdit && (isCreator || isOwner || isAdmin);

  const submitEdit = async () => {
    if (!onEdit) return;
    const patch = {};
    if (editForm.name.trim() && editForm.name.trim() !== member.name) patch.name = editForm.name.trim();
    if (editForm.role_or_relationship && editForm.role_or_relationship !== member.role_or_relationship) {
      patch.role_or_relationship = editForm.role_or_relationship;
    }
    const dy = editForm.death_year === "" ? null : Number(editForm.death_year);
    if (dy !== member.death_year) patch.death_year = dy;
    const ok = await onEdit(member, patch);
    if (ok !== false) setEditing(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md" data-testid="member-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-forest sr-only">{member.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <Avatar className={`w-20 h-20 border-2 ${isMemorial ? "border-[#A8A8B0]" : isOpenSeat ? "border-[#E89C3F]" : "border-gold/60"}`}>
              {photo && <AvatarImage src={fileUrl(photo)} alt={member.name} />}
              <AvatarFallback className="bg-forest text-ivory text-2xl">{initial}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-display text-2xl text-forest leading-tight" data-testid="member-detail-name">{member.name}</h3>
                {canEdit && !editing && (
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="text-forest/70 hover:text-forest hover:bg-cream rounded-full p-1.5 transition-colors shrink-0"
                    title="Edit this placeholder"
                    data-testid="member-detail-edit-button"
                  >
                    <PencilSimple size={16} weight="bold" />
                  </button>
                )}
              </div>
              {member.maiden_name && <p className="text-sm italic text-sageGray">née {member.maiden_name}</p>}
              {subline && <p className="text-sm text-forest font-medium mt-0.5" data-testid="member-detail-relationship">{subline}</p>}
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {role === "owner" && <Badge className="bg-gold/20 text-forest border-0 rounded-full text-[10px] uppercase tracking-widest"><Crown size={11} className="mr-1" weight="fill" /> owner</Badge>}
                {role === "admin" && <Badge className="bg-forest/10 text-forest border-0 rounded-full text-[10px] uppercase tracking-widest"><ShieldCheck size={11} className="mr-1" weight="fill" /> admin</Badge>}
                {role === "branch_leader" && <Badge className="bg-gold/15 text-forest border-0 rounded-full text-[10px] uppercase tracking-widest"><ShieldCheck size={11} className="mr-1" weight="fill" /> branch leader · {branch}</Badge>}
                {branch && role !== "branch_leader" && <Badge className="bg-cream text-sageGray border border-sage rounded-full text-[10px] uppercase tracking-widest">{branch} side</Badge>}
                {isMemorial && <Badge className="bg-[#F4F4F2] text-sageGray border border-[#A8A8B0] rounded-full text-[10px] uppercase tracking-widest">in memoriam</Badge>}
                {isOpenSeat && memberType === "org_role" && <Badge className="bg-[#FFF6E5] text-[#8A5A1F] border border-[#E89C3F]/40 rounded-full text-[10px] uppercase tracking-widest" data-testid="member-detail-open-seat">open seat</Badge>}
                {isOpenSeat && memberType !== "org_role" && <Badge className="bg-[#FFF6E5] text-[#8A5A1F] border border-[#E89C3F]/40 rounded-full text-[10px] uppercase tracking-widest">not yet joined</Badge>}
                {isCommunityVerified && <Badge className="bg-forest/10 text-forest border border-forest/30 rounded-full text-[10px] uppercase tracking-widest" data-testid="member-detail-family-verified"><ShieldCheck size={11} className="mr-1" weight="fill" /> Verified by family</Badge>}
                {isPendingApproval && <Badge className="bg-[#FFF6E5] text-[#8A5A1F] border border-[#E89C3F]/40 rounded-full text-[10px] uppercase tracking-widest" data-testid="member-detail-pending-approval">pending owner approval</Badge>}
              </div>
            </div>
          </div>

          {editing && canEdit && (
            <div className="border border-forest/30 rounded-xl p-4 bg-cream/60 space-y-3" data-testid="member-detail-edit-form">
              <p className="text-xs uppercase tracking-widest text-forest font-medium">Edit placeholder</p>
              <div>
                <Label className="text-forest text-xs">Name</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="mt-1 bg-white border-sage focus-visible:ring-gold"
                  data-testid="member-detail-edit-name"
                />
              </div>
              <div>
                <Label className="text-forest text-xs">Relationship</Label>
                <Select value={editForm.role_or_relationship} onValueChange={(v) => setEditForm({ ...editForm, role_or_relationship: v })}>
                  <SelectTrigger className="mt-1 bg-white border-sage" data-testid="member-detail-edit-role"><SelectValue placeholder="Choose a relationship" /></SelectTrigger>
                  <SelectContent>
                    {PLACEHOLDER_ROLE_OPTIONS.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-forest text-xs">Year passed (optional)</Label>
                <Input
                  type="number"
                  value={editForm.death_year}
                  onChange={(e) => setEditForm({ ...editForm, death_year: e.target.value })}
                  className="mt-1 bg-white border-sage focus-visible:ring-gold"
                  placeholder="e.g. 1998"
                  data-testid="member-detail-edit-death-year"
                />
                <p className="text-[10px] text-sageGray mt-1">Setting a year unlocks the Obituary + Personal photo slots.</p>
              </div>
              <div className="flex items-center justify-end gap-2 pt-1">
                <Button size="sm" variant="ghost" disabled={savingEdit} onClick={() => setEditing(false)} className="text-sageGray rounded-full" data-testid="member-detail-edit-cancel">Cancel</Button>
                <Button size="sm" disabled={savingEdit} onClick={submitEdit} className="bg-forest hover:bg-forestSoft text-ivory rounded-full" data-testid="member-detail-edit-save">
                  <Check size={14} className="mr-1" weight="bold" /> {savingEdit ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
          )}
          {showApprovalBlock && (
            <div className="border-2 border-[#E89C3F]/70 rounded-xl p-4 bg-[#FFF8EC]" data-testid="member-detail-approval-block">
              <p className="text-sm text-[#8A5A1F] font-medium">Extended-family placeholder needs your OK</p>
              <p className="text-xs text-sageGray mt-1">
                A verified member added this person but they're not a direct relative of yours. Approve to show them on the family map.
              </p>
              <div className="flex items-center gap-2 mt-3">
                <Button size="sm" disabled={approving} onClick={() => onApprove?.(member)} className="bg-forest hover:bg-forestSoft text-ivory rounded-full" data-testid="member-detail-approve">{approving ? "Approving…" : "Approve"}</Button>
                <Button size="sm" disabled={approving} onClick={() => onReject?.(member)} variant="outline" className="rounded-full border-red-300 text-red-700 hover:bg-red-50" data-testid="member-detail-reject">Reject</Button>
              </div>
            </div>
          )}

          {showConfirmBlock && !isCommunityVerified && (
            <div className="border border-forest/20 rounded-xl p-4 bg-cream/70" data-testid="member-detail-confirm-block">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-forest font-medium flex items-center gap-1.5"><ShieldCheck size={14} weight="duotone" /> Family verification</p>
                <span className="text-xs text-sageGray tabular-nums" data-testid="member-detail-confirm-count">{confirmationsCount} / {confirmationsRequired}</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-sage/50 overflow-hidden">
                <div className="h-full bg-forest transition-all" style={{ width: `${Math.min(100, Math.round((confirmationsCount / confirmationsRequired) * 100))}%` }} />
              </div>
              <p className="text-xs text-sageGray mt-2">
                {confirmationsRequired} verified family members must confirm before this placeholder is marked "Verified by family".
              </p>
              {!isCreator && !alreadyConfirmed && (
                <Button size="sm" disabled={confirming} onClick={() => onConfirm?.(member)} className="bg-forest hover:bg-forestSoft text-ivory rounded-full mt-3" data-testid="member-detail-confirm-button">
                  {confirming ? "Confirming…" : "I know this person"}
                </Button>
              )}
              {alreadyConfirmed && (
                <p className="text-xs text-forest font-medium mt-3" data-testid="member-detail-confirm-already">You've confirmed this placeholder.</p>
              )}
              {isCreator && (
                <p className="text-xs italic text-sageGray mt-3">You added this placeholder — others need to confirm.</p>
              )}
            </div>
          )}

          {isOpenSeat && onClaim && (
            <div className="border-2 border-dashed border-[#E89C3F]/60 rounded-xl p-4 bg-[#FFF8EC]" data-testid="member-detail-claim-block">
              <p className="text-sm text-[#8A5A1F] font-medium flex items-center gap-2">
                <HandPointing size={16} weight="duotone" /> Is this you — {member.name}?
              </p>
              <p className="text-xs text-sageGray mt-1">
                {memberType === "org_role"
                  ? "Claiming this seat binds it to your profile so the chart shows your photo here."
                  : "Claiming will transfer every connection drawn through this placeholder onto your profile. This can't be undone."}
              </p>
              <div className="flex items-center gap-2 mt-3">
                <Button
                  size="sm"
                  onClick={() => onClaim(member)}
                  disabled={claiming}
                  className="bg-forest hover:bg-forestSoft text-ivory rounded-full"
                  data-testid="member-detail-claim-confirm"
                >
                  {claiming ? "Claiming…" : "Yes, this is me"}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                  className="text-sageGray rounded-full"
                  data-testid="member-detail-claim-cancel"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {member.bio ? (
            <div className="border-t border-sage pt-4">
              <p className="text-xs uppercase tracking-widest text-sageGray mb-2">Bio</p>
              <p className="text-charcoal whitespace-pre-line leading-relaxed" data-testid="member-detail-bio">{member.bio}</p>
            </div>
          ) : (
            <div className="border-t border-sage pt-4">
              <p className="text-sm text-sageGray italic" data-testid="member-detail-no-bio">
                {isMemorial ? "No biography on file yet." : isOpenSeat ? "No bio yet — this spot hasn't been claimed." : "Hasn't added a bio yet."}
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
